import {
  ToolTipData,
  DrawToolTipArgs,
  CoreSize, CanvasSets,
  CoreTransform,
  ContainerBox,
  MapChanges,
  NodeEl,
  LinkEl,
  LinkSet,
  NodeElOpt,
  LinkElOpt,
  Animation,
  HasIdEl,
  Cordinate,
  NodeLinks,
  LinkDraw,
  PointLookupResult,
  BundleDraw,
  NodeLinkChoice,
  LinkRenderCache,
  LinkRenerIndex
} from "./CommonTypes";
import CalculatorBase, { CORE_R, FULL_CIRCLE, } from "./CalculatorBase";
import { THEME_MAP, ThemeOptionSets } from "./THEME_MAP";
import ToolTipsProps from "./ToolTipsProps";
import Indexer from "./Indexer";


interface LinkSets { [key: string]: LinkSet }


interface SetCalculatorData {
  toolTipData?: ToolTipData,
  changes?: MapChanges;
  grid?: boolean;
  theme?: string;
  transform?: CoreTransform;
  themes?: ThemeOptionSets;
  r?: number;
  autoFit?: boolean,
  noChange?: boolean;
  size?: CoreSize;
  nodes: NodeEl[];
  links: LinkEl[];
  nodeOpts?: { [optId: string]: NodeElOpt };
  linkOpts?: { [option: string]: LinkElOpt };
  autoToolTip?: boolean;

  // toolbar control
  noTools?: boolean;
  hideCompass?: boolean;
  hideFullScreen?: boolean;
  showReset?: boolean;
  hideGridToggle?: boolean;
  hideSearch?: boolean;
  hideZoomAndRestore?: boolean;
  tick?: number;
  renderToolTipOptions?: ToolTipsProps;
}


const SD: ['s', 'd'] = ['s', 'd']
const CORE_SIZE = { width: 1920, height: 1080 }

const CORE_TRANSFORM = { x: 0, y: 0, k: 1 };

export { type SetCalculatorData }
interface NodeToLinkMap {
  [key: string]: { [key: string]: LinkSet }
}

interface DragGroups {
  [nodeElId: string]: string[]
}

export default class Calculator extends CalculatorBase {
  rebuild = false;
  createCache = false;
  doDraw = true;
  dragGroups: DragGroups = {};
  nodeToLinkMap: NodeToLinkMap = {}
  linkRenderCache: LinkRenderCache[] = [];
  linkRenderIndex: LinkRenerIndex = {};
  showReset: boolean = false;
  autoToolTip: boolean = true;
  needsIndexing: boolean = false;
  toolTipData: ToolTipData = {};
  linkCache: { [nodeId: string]: ContainerBox } = {}
  highlight = false;
  textAlign: string = 'center';
  fontFamily: string = 'Arial';
  color: string = 'black';
  bgColor: string = 'white';
  bundleColor: string = 'darkgrey';
  lineColor: string = 'black';
  fillColor: string = 'white';
  theme: string = 'light';
  shadeColor: string = 'lightgrey';
  indexer: Indexer = new Indexer();

  mouseOverColor: string = 'lightblue';
  stroke: string = 'black';
  fill: string = 'black';
  contexts: { [key: string]: CanvasRenderingContext2D } = {};
  needsMinMax: boolean = false;
  minMax = {
    minX: NaN,
    minY: NaN,
    maxX: NaN,
    maxY: NaN,
  }
  animations: Animation[] = [];
  fit: boolean = false;
  canvases: CanvasSets = {
    ctrl: null, nodes: null, bundles: null, animations: null, links: null
  }
  links: LinkSets = {};
  nodes: { [id: string]: NodeEl } = {};
  nodeLinks: NodeLinks = {};
  rawLinks: { [id: string]: LinkEl } = {};
  nodeOpts: { [optId: string]: NodeElOpt } = {}
  alpha = 1;
  shadeAlpha = .5;
  linkOpts: { [option: string]: LinkElOpt } = {}
  images: { [key: string]: { img: HTMLImageElement, n: NodeEl[], loaded: boolean } } = {};
  imgSize = 24;
  fontSize = 10;
  drag: boolean = false;
  srcNodes: NodeEl[] = [];
  srcLinks: LinkEl[] = [];
  indexSize: number = 48;
  initSize: CoreSize = CORE_SIZE;
  shadeR = 16.97;
  tick = 0;
  ticks = 40;
  tickSlots = 3;
  tickSpace = 4;
  transform: CoreTransform = { ...CORE_TRANSFORM };
  changes: MapChanges = { nodes: {}, transform: this.transform, grid: false, tick: 0 };
  noChange: boolean = false;
  lineWidth = 1;
  boxWidth = 21.6;
  boxR = 5.4;
  mounted = false;
  timeout: NodeJS.Timeout | undefined;
  animationTimer = 500;
  themes: ThemeOptionSets = THEME_MAP;

  setTheme(theme: string = this.theme, themes: ThemeOptionSets = this.themes) {
    this.themes = themes;
    this.theme = theme;
    Object.assign(this, themes[theme]);
  }

  computeLinePoint(s: Cordinate, r: number, a: number, slots: number, pos: number) {
    const scale = 1 / (slots + 1);
    const next = r * (pos + 1) * scale;
    return this.getXY(s.x, s.y, next, a)
  }


  setData(dataSet: SetCalculatorData) {
    const { tick, showReset, autoToolTip, toolTipData, changes, themes, theme, autoFit, noChange, size, nodes, links, nodeOpts, linkOpts, r, transform, grid } = dataSet;
    if (typeof theme != 'undefined') this.theme = theme;
    if (tick) this.tick = this.changes.tick = tick;
    if (typeof themes !== 'undefined') this.themes = themes;
    Object.assign(this, this.themes[this.theme]);
    this.srcNodes = nodes || [];
    this.srcLinks = links || [];
    this.r = r || CORE_R;
    this.transform = transform || this.transform || { ...CORE_TRANSFORM };
    this.nodeOpts = nodeOpts || this.nodeOpts || {};
    this.linkOpts = linkOpts || this.linkOpts || {};
    this.initSize = size || CORE_SIZE;
    this.fit = autoFit || false;
    this.noChange = noChange || false;
    this.setR();
    this.changes.nodes = {};
    this.changes.grid = grid || false;
    if (typeof autoToolTip !== 'undefined') this.autoToolTip = autoToolTip;
    if (typeof showReset !== 'undefined') this.showReset = showReset;
    this.changes.transform = this.transform;
    this.toolTipData = toolTipData || {};
    if (changes) {
      Object.assign(this.changes, changes);
      this.changes.transform = this.transform = changes.transform;
    }
    this.needsMinMax = false;
    this.buildData();
  }

  indexTodo: { Cs: ContainerBox, target: NodeLinkChoice, obj: HasIdEl }[] = [];

  buildFullIndex() {
    if (!this.needsIndexing) return;
    const indexTodo = this.indexTodo;
    this.indexer.indexSize = this.indexSize;
    this.indexer.buildIndexes(indexTodo);
    this.indexTodo = [];
  }

  buildIndex(Cs: ContainerBox, target: NodeLinkChoice, obj: HasIdEl) {
    if (this.drag) return;
    this.indexTodo.push({ Cs, target, obj });
  }

  getIndex(p: Cordinate, t = this.transform) {
    const tp = this.translateCanvasYX(p, t);
    this.buildFullIndex()
    const res = this.indexer.lookup(tp);

    return res != null ? { ...res, tp } : { tp, nodes: undefined, links: undefined };
  }

  getChanges() {
    return this.changes;
  }

  onMount() {
    if (this.mounted) return;
    this.mounted = true;
  }

  onUnMount() {
    if (!this.mounted) return;
    this.mounted = false
    clearTimeout(this.timeout);
  }

  setTransform(t: CoreTransform) {
    if (this.noChange) return;
    Object.assign(this.transform, t);
    this.draw();
  }

  forceDraw(size = this.initSize) {
    this.drag = false;
    this.draw(size);
  }

  draw(size = this.initSize) {
    this.initSize = size;
    if (this.mounted) {
      this.drawNodes();
    }
  }

  scaleSize(scale: number, size = this.initSize) {
    if (this.noChange) return;
    this.fit = false;
    if (scale < .01) return;
    this.transform.k = scale;
    this.draw(size)
  }

  redoMinMax() {
    if (!this.needsMinMax) return;
    this.needsMinMax = false;
    const { minMax, nodes } = this;
    let i = 0;
    for (const id in nodes) {
      const node = nodes[id];
      const minX = node.x
      const minY = node.y
      const maxX = node.x
      const maxY = node.y
      if (i++ == 0) {
        Object.assign(minMax, { minX, minY, maxX, maxY });
        continue;
      }
      if (maxX > minMax.maxX) minMax.maxX = maxX
      if (maxY > minMax.maxY) minMax.maxY = maxY
      if (minX < minMax.minX) minMax.minX = minX;
      if (minY < minMax.minY) minMax.minY = minY;
    }
  }

  createCenertTransform(minMax = this.minMax, offset = this.imgSize * 2.7) {
    const { width, height } = this.initSize;
    const h = Math.abs(minMax.maxY - minMax.minY + offset);
    const w = Math.abs(minMax.maxX - minMax.minX + offset);
    const scaleX = width / w
    const scaleY = height / h;
    const scale = scaleX < scaleY ? scaleX : scaleY;
    const ox = width * .5 - w * .5 * scale;
    const oy = height * .5 - h * .5 * scale;
    return {
      k: scale,
      x: ox - (minMax.minX - offset * .5) * scale,
      y: oy - (minMax.minY - offset * .5) * scale,
    }
  }

  fitSize() {
    if (this.noChange) return;
    Object.assign(this.transform, this.createCenertTransform());
  }

  clearCtrl(clear = true) {
    if (!clear) return;

    if (this.highlight) {
      const { ctrl } = this.contexts;
      const { x, y, k } = this.transform
      ctrl.setTransform(1, 0, 0, 1, 0, 0);
      ctrl.clearRect(0, 0, this.initSize.width, this.initSize.height);
      ctrl.setTransform(k, 0, 0, k, x, y);
    }
    this.highlight = false;
  }

  drawHighlight(res: PointLookupResult, p: Cordinate) {
    this.clearCtrl(true);
    this.highlight = true;

    const { ctrl } = this.contexts;
    ctrl.globalAlpha = this.shadeAlpha;
    if (res.node) {
      this.drawNodeHighlght(ctrl, res.node);
      this.drawToolTip({ id: 'node-' + res.node.i, ...p })
    } else if (res.link) {
      this.drawLinkHighlight(ctrl, res.link);
      this.drawToolTip({ id: 'link-' + res.link.l.i, ...p });
    } else if (res.bundle) {
      const { bundle } = res;
      this.drawBundleHighlight(ctrl, bundle);
      this.drawToolTip({ id: 'bundle-' + bundle.i, ...p });
    } else {
      this.highlight = false;
    }
    ctrl.globalAlpha = this.alpha;
  }

  drawCenteredOnNode(node: NodeEl) {
    if (!node) return;
    const n = this.nodes[node.i];
    const { width, height } = this.initSize;

    const { k } = this.transform;
    const x = width * .5 - n.x * k
    const y = height * .5 - n.y * k
    Object.assign(this.transform, { x, y });
    this.draw();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  drawToolTip = (args: DrawToolTipArgs) => { }

  drawNodeHighlght(ctrl: CanvasRenderingContext2D, node: NodeEl) {
    this.drawCircle(
      ctrl,
      node,
      this.shadeColor,
      this.shadeR,
      this.shadeColor
    )
  }

  drawLinkHighlight(ctrl: CanvasRenderingContext2D, link: LinkDraw) {
    const lr = link.r || this.r;
    this.drawLine(ctrl,
      link.s,
      link.d,
      this.shadeColor,
      lr * 3
    )
    const { r, imgSize } = this;
    const { s, d } = link.l;
    if (!this.nodes[s].h) {
      const n = this.nodes[s]
      ctrl.clearRect(n.x - r, n.y - r, imgSize, imgSize)
      this.drawNodeHighlght(ctrl, n)
    }

    if (!this.nodes[d].h) {
      const n = this.nodes[d]
      ctrl.clearRect(n.x - r, n.y - r, imgSize, imgSize)
      this.drawNodeHighlght(ctrl, n)
    }
  }

  drawBundleHighlight(ctrl: CanvasRenderingContext2D, bundle: BundleDraw) {
    const { s, d, l, lr, c, r } = bundle
    for (let i = 0; i < l!.length; ++i) {
      const link = l![i];
      this.drawLine(ctrl,
        link.s,
        link.d,
        this.shadeColor,
        lr * 3
      )
    }
    if (!this.nodes[s].h) {
      const { r, imgSize } = this;
      const n = this.nodes[s]
      ctrl.clearRect(n.x - r, n.y - r, imgSize, imgSize)
      this.drawNodeHighlght(ctrl, n)
    }

    if (!this.nodes[d].h) {
      const { r, imgSize } = this;
      const n = this.nodes[d]
      ctrl.clearRect(n.x - r, n.y - r, imgSize, imgSize)
      this.drawNodeHighlght(ctrl, n)
    }

    this.drawCircle(
      ctrl,
      c,
      this.shadeColor,
      r + lr,
      this.shadeColor
    )
  }

  drawNodes() {

    const { width, height } = this.initSize;
    const { srcNodes } = this;
    if (this.fit && srcNodes.length != 0) {
      const noChange = this.noChange;
      this.noChange = false;
      this.fitSize();
      this.noChange = noChange;
    }
    // always force the fit state to false
    this.fit = false;
    const contexts: { [key: string]: CanvasRenderingContext2D } = {};
    this.contexts = contexts;
    const { x, y, k } = this.transform
    const { r, nodes } = this;
    for (const [name, canvas] of Object.entries(this.canvases)) {
      const context = contexts[name] = canvas.getContext("2d");
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, width, height);
      context.textAlign = this.textAlign;
      context.font = this.fontSize + 'px ' + this.fontFamily;
      canvas.width = width;
      canvas.height = height;
      context.setTransform(k, 0, 0, k, x, y);
    }
    if (this.createCache) {
      this.needsIndexing = true;
      this.indexTodo = [];
      this.linkCache = {};
      this.animations = [];
      const drawnLinks: { [key: string]: number } = {};
      for (let i = srcNodes.length - 1; i > -1; --i) {
        const node = nodes[srcNodes[i].i];

        if (!node.h) {
          this.drawNode(node);
          if (!this.drag) {
            const box = this.createNodeBox(node, r);
            this.buildIndex(box, 'nodes', node);
          }
        }
        const links = this.nodeLinks[node.i];
        if (!links) continue;
        const { order, sets } = links;
        for (let idx = order.length - 1; idx > -1; --idx) {
          const key = order[idx];
          if (drawnLinks[key]) continue;
          drawnLinks[key] = 1;
          this.drawLink(sets[key]);
        }
      }
      this.createCache = false;
    } else {
      for (let i = srcNodes.length - 1; i > -1; --i) {
        const node = nodes[srcNodes[i].i];
        if (!node.h) {
          this.drawNode(node);
        }
      }
      const { links, bundles } = this.contexts;
      const { linkRenderCache } = this;
      for (let i = 0; i < linkRenderCache.length; ++i) {
        const todo = linkRenderCache[i];
        const l = todo.links;
        const b = todo.bunldes;
        for (let id = 0; id < l.length; ++id) {
          const { start, end, c, w } = l[id];
          this.drawLine(links, start, end, c, w);
        }
        for (let id = 0; id < b.length; ++id) {
          const { c, p, bR } = b[id];
          this.drawCircle(bundles, p, c, bR);
        }
      }

    }
    this.drawAllAnimations();
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.onAnimate, this.animationTimer);
  }

  drawAllAnimations() {
    const todo = this.animations;
    if (todo.length == 0) return;

    const { width, height } = this.initSize;
    const { animations } = this.contexts;
    animations.setTransform(1, 0, 0, 1, 0, 0);
    animations.clearRect(0, 0, width, height);
    const { x, y, k } = this.transform;
    animations.setTransform(k, 0, 0, k, x, y);
    const tick = this.tick;
    for (let i = 0; i < todo.length; ++i) {
      this.tick += i;
      this.drawAinimation(animations, todo[i]);
    }
    this.tick = tick;
  }
  onAnimate = () => {
    clearTimeout(this.timeout);
    this.changes.tick = ++this.tick;
    this.drawAllAnimations();
    this.timeout = setTimeout(this.onAnimate, this.animationTimer);
  }

  drawNode(node: NodeEl) {
    const opts = this.nodeOpts[node.o];
    const { nodes } = this.contexts;
    this.drawText(nodes, node, node.l);
    if (opts.i) {
      this.drawImage(nodes, node, opts.i)
    } else {
      this.drawBox(nodes, node, opts.c || '');
    }
  }

  drawLink(ls: LinkSet) {
    if (ls.l.length == 0) return;
    const { l, n, b } = ls;
    const w = this.getLineWith(ls.l.length);
    const p = this.nodes[n.s]
    const c = this.nodes[n.d]
    const ba = this.GetAngle(p.x, p.y, c.x, c.y);
    const da = this.GetAngle(c.x, c.y, p.x, p.y);
    const angle = ba + 270;
    const { linkOpts, boxWidth, boxR } = this;
    const ne = this.getXY(c.x, c.y, boxR, angle);
    const nw = this.getXY(p.x, p.y, boxR, angle);
    const sa = angle + 180;
    const { links, bundles } = this.contexts
    const incBy = w + boxWidth / (2 * l.length + 1);
    const o = w + w * .5;
    const ll = ls.ll = [] as LinkDraw[];
    const d = this.getDistance(p.x, p.y, c.x, c.y);
    ls.lr = w * .5;
    const renderSet: LinkRenderCache = {
      links: [],
      bunldes: [],
    }
    for (let i = 0; i < l.length; ++i) {
      const link = l[i];
      const r = o + i * incBy;
      const start = this.getXY(ne.x, ne.y, r, sa);
      const end = this.getXY(nw.x, nw.y, r, sa);
      const c = linkOpts[link.o].c

      if (this.doDraw) this.drawLine(links, start, end, c, w);
      const lmv: LinkDraw = { i: link.i, s: start, d: end, l: link };
      ll.push(ls.lm[link.i] = lmv);
      const as = { s: { a: ba, s: start }, d: { a: da, s: end } };
      let aid = this.rebuild ?
        this.linkRenderCache[this.linkRenderIndex[ls.key]].links[i].aid :
        this.animations.length;

      renderSet.links.push({ start, end, c, w, aid });
      if (link.a) {
        const { a } = link;
        if (a == 's' || a == 'd') {
          /** @type {Animation} */
          const animate: Animation = {
            ...as[a],
            r: d,
            c,
            f: c,
            w: w * .75,
            o: i,
          }
          this.animations[aid++] = animate;

        } else if (a == 'b') {
          for (let i = 0; i < SD.length; ++i) {
            const a = SD[i];
            /** @type {Animation} */
            const animate: Animation = {
              ...as[a],
              r: d,
              c,
              o: i,
              f: i ? c : this.bgColor,
              w: w * .75,
            }
            this.animations[aid++] = animate;
          }
        }
      }
    }
    const se = this.getXY(c.x, c.y, boxR, sa);
    const sw = this.getXY(p.x, p.y, boxR, sa);
    const bl = ls.bl = [] as { c: Cordinate, b: string }[];
    if (!this.drag) {
      const box = ls.box = { ne, nw, se, sw }
      this.buildIndex(box, 'links', { i: ls.key });
    }
    const bR = boxR - w * .5;
    ls.br = bR;
    for (let i = 0; i < b.length; ++i) {
      const pos = this.computeLinePoint(p, d, ba + 180, b.length, i);
      bl.push({ c: pos, b: b[i] });

      renderSet.bunldes.push({ p: pos, c: this.bundleColor, bR })
      if (this.doDraw) this.drawCircle(bundles, pos, this.bundleColor, bR)

      const bi = bR / 1.5;
      if (this.doDraw) this.drawCircle(bundles, pos, this.bundleColor, bi)
      renderSet.bunldes.push({ p: pos, c: this.bundleColor, bR: bi })

      const bx = bR / 4;
      if (this.doDraw) this.drawCircle(bundles, pos, this.bundleColor, bx)
      renderSet.bunldes.push({ p: pos, c: this.bundleColor, bR: bx })
    }
    const next = this.rebuild ? this.linkRenderIndex[ls.key] : this.linkRenderCache.length;
    this.linkRenderCache[next] = renderSet;
    this.linkRenderIndex[ls.key] = next;
  }

  drawAinimation(context: CanvasRenderingContext2D, animation: Animation) {
    const { a, c, f, r, s, w, o } = animation
    const { tick, tickSlots, ticks, tickSpace } = this;
    for (let i = 0; i < tickSlots; ++i) {
      const tc = (o + tick + (i + 1) * tickSpace) % ticks;
      const pos = this.computeLinePoint(s, r, a, ticks, tc);
      this.drawCircle(context, pos, c, w, f)
    }
  }

  setR(r = this.r) {
    this.r = r;
    this.imgSize = r * 2;
    this.indexSize = r * 4;
    this.boxWidth = this.r * 1.3;
    this.boxR = this.boxWidth * .5;
    this.shadeR = .5 * this.getDistance(0, 0, this.imgSize, this.imgSize);
    this.fontSize = Math.round(r * 5 / 6);
  }

  createNodeBox(c: Cordinate, r: number) {
    const o: ContainerBox = { ne: { x: 0, y: 0 }, nw: { x: 0, y: 0 }, se: { x: 0, y: 0 }, sw: { x: 0, y: 0 }, };
    o.ne.x = o.se.x = c.x + r;
    o.nw.x = o.sw.x = c.x - r;
    o.se.y = o.sw.y = c.y + r;
    o.nw.y = o.ne.y = c.y - r;
    return o;
  }

  createLinkBox(s: Cordinate, d: Cordinate, r = this.lineWidth) {
    const angle = this.GetAngle(
      s.x,
      s.y,
      d.x,
      d.y
    );
    const angle_right = (angle + 90);
    const angle_left = (angle_right + 180);
    const ne = this.getXY(s.x, s.y, r, angle_right);
    const nw = this.getXY(s.x, s.y, r, angle_left);
    const se = this.getXY(d.x, d.y, r, angle_right);
    const sw = this.getXY(d.x, d.y, r, angle_left);
    const cb: ContainerBox = { ne, nw, se, sw };
    return cb;
  }



  defaultLookupResult(tp?: Cordinate) {
    let co: Cordinate = { x: NaN, y: NaN };
    if (typeof tp != 'undefined') co = tp;
    const res: PointLookupResult = {
      type: 'none',
      bundle: null,
      node: null,
      link: null,
      tp: co,
    }
    return res;
  }

  lookupPoint(p: Cordinate) {
    this.buildFullIndex();
    const el = this.getIndex(p);
    const res = this.defaultLookupResult(el.tp);

    if (Object.keys(el).length == 1) return res;
    const { tp, nodes, links } = el;
    for (let i = 0; nodes && i < nodes.length; ++i) {
      const node = this.nodes[nodes[i]];
      if (this.insideSquare(node, tp)) {
        res.node = node || null;
        res.type = 'node';
        return res;
      }
    }
    for (let i = 0; links && i < links.length; ++i) {
      const key = links[i];
      const nl = this.links[key] as LinkSet;
      if (this.insideBox(nl.box!, tp)) {
        for (let i = 0; i < nl.bl!.length; ++i) {
          const { b, c } = nl.bl![i];
          if (this.insideCircle(tp, c, nl.br!)) {
            res.type = 'bundle';
            const bundle = res.bundle = {
              b,
              ...nl.n,
              p: i,
              r: nl.br!,
              i: nl.key + ',' + b,
              c,
              lr: nl.lr!,
              l: [] as LinkDraw[],
            }
            const links = bundle.l;
            const s = nl.s[b];
            const { lm } = nl;
            for (let i = 0; i < s.length; ++i) {
              links.push(lm[s[i]]);
            }
            return res;
          }

        }
        const { ll, l, lr } = nl;
        for (let i = 0; i < ll.length; ++i) {
          const src = ll[i];
          const box = this.linkCache[src.i!] || (this.linkCache[src.i!] = this.createLinkBox(src.s, src.d, lr));
          if (this.insideBox(box, tp)) {
            res.link = { l: l[i], s: src.s, d: src.d, r: lr };
            res.type = 'link';
            return res;
          }
        }
      }
    }
    return res;
  }


  drawLine(context: CanvasRenderingContext2D, s: Cordinate, d: Cordinate, c: string, w: number) {
    context.beginPath();
    context.lineWidth = w;

    context.strokeStyle = c;
    context.moveTo(s.x, s.y);
    context.lineTo(d.x, d.y);
    context.closePath();
    context.stroke();
  }

  drawImage(context: CanvasRenderingContext2D, node: NodeEl, src: string) {
    if (this.images[src]) {
      if (this.images[src].loaded) {
        context.drawImage(this.images[src].img, node.x - this.r, node.y - this.r, this.imgSize, this.imgSize);
      } else {
        this.images[src].n.push(node);
      }
    } else {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        this.images[src].loaded = true;
        if (!this.mounted) return;

        const nodes = this.images[src].n;
        for (let i = 0; i < nodes.length; ++i) {
          const node = nodes[i];
          context.drawImage(this.images[src].img, node.x - this.r, node.y - this.r, this.imgSize, this.imgSize);
        }
      }
      this.images[src] = {
        loaded: false,
        img,
        n: [node],
      }
    }
  }

  drawCircle(context: CanvasRenderingContext2D, n: Cordinate, c: string, r = this.r, f?: string) {
    context.beginPath();
    if (f) context.fillStyle = f;
    context.strokeStyle = c;
    context.lineWidth = this.lineWidth;
    context.arc(n.x, n.y, r, 0, FULL_CIRCLE);
    if (f) context.fill();
    context.stroke();
    context.closePath();
  }

  drawBox(context: CanvasRenderingContext2D, n: Cordinate, c: string, r = this.r, imgSize = this.imgSize) {
    context.fillStyle = c;
    context.rect(n.x - r, n.y - r, imgSize, imgSize)
    context.fill();
  }

  drawText(context: CanvasRenderingContext2D, n: Cordinate, l: string) {
    context.fillStyle = this.color;
    const meta = context.measureText(l);
    const xo = meta.width / -2;
    const yo = (meta.actualBoundingBoxAscent + meta.actualBoundingBoxDescent) / 2 + this.r;
    context.fillText(l, n.x + xo, n.y - yo);
  }


  getLineWith(w:number ) {
    return this.boxWidth / (w * 2 + 1);
  }

  buildData() {
    this.indexer.reset();
    this.linkRenderCache = [];
    this.linkRenderIndex = {};
    const nodeLinkMap: NodeToLinkMap = this.nodeToLinkMap = {};
    this.rebuild = false;
    this.createCache = true;
    const links: LinkSets = {};
    const nodes: { [id: string]: NodeEl } = {};
    const nodeLinks: NodeLinks = {};
    const { linkOpts, nodeOpts, minMax, srcNodes, srcLinks, autoToolTip, toolTipData } = this;
    const dragGroups: DragGroups = this.dragGroups = {};

    for (const img of Object.values(this.images)) {
      img.n = [];
    }
    for (let i = 0; i < srcNodes.length; ++i) {
      const node = srcNodes[i];
      nodeLinkMap[node.i] = {};
      if (!nodeOpts[node.o]) throw new Error(`Bad node options, check value of: o,  in srcNodes[${i}, o must exist in nodeOpts]`)
      if (!nodeOpts[node.o].i && !nodeOpts[node.o].c) throw new Error(`No image or color option set for node: srcNodes[${i}] in nodeOpts for ${node.o}`);
      if (nodes[node.i]) throw new Error(`Duplicate node: [${node.i}] in srcNodes[${i}]`);
      if (node.g) (dragGroups[node.g] || (dragGroups[node.g] = [])).push(node.i);

      nodes[node.i] = node;
      if (i == 0) {
        minMax.minX = node.x
        minMax.minY = node.y

        minMax.maxX = node.x
        minMax.maxY = node.y
      } else {

        if (node.x > minMax.maxX) minMax.maxX = node.x
        if (node.y > minMax.maxY) minMax.maxY = node.y
        if (node.x < minMax.minX) minMax.minX = node.x;
        if (node.y < minMax.minY) minMax.minY = node.y;
      }
      const ttKey = `node-${node.i}`;
      if (autoToolTip && !Object.hasOwnProperty.call(toolTipData,ttKey)) {
        toolTipData[ttKey] = { label: node.l }
      }
    }
    const autBundleTT: { [key: string]: boolean } = {};
    const sane: { [id: string]: LinkEl } = this.rawLinks = {};
    for (let i = 0; i < srcLinks.length; ++i) {
      const link = srcLinks[i];
      if (sane[link.i]) throw new Error(`Duplicate link.i ${link.i} in srcLinks[${i}`);
      sane[link.i] = link;
      if (!linkOpts[link.o]) throw new Error(`missing linkOpt in: srcLinks[${i}]`)
      if (!nodes[link.s] || !nodes[link.d] || link.s == link.d)
        throw new Error(`Bad link in srcNodes[${i}], missing node(s)`)

      const key = link.s > link.d ? (link.d + ',' + link.s) : (link.s + ',' + link.d);
      const set = links[key] || (links[key] = { bl: [], ll: [], lm: {}, l: [], b: [], s: {}, key, n: { s: link.s, d: link.d } });
      const { sets, order } = (nodeLinks[link.s] || (nodeLinks[link.s] = { sets: {}, order: [] }));

      nodeLinkMap[link.s][link.i] = set
      nodeLinkMap[link.d][link.i] = set;

      if (!sets[key]) {
        sets[key] = set;
        order.push(key);
      }
      const ttKey = `link-${link.i}`;
      if (autoToolTip && !Object.hasOwnProperty.call(toolTipData,ttKey)) {
        toolTipData[ttKey] = { label: link.l || link.i };
      }
      const { l, b, s } = set;
      l.push(link);
      if (!link.b) continue;
      for (const tb of link.b) {
        const ttKey = `bundle-${key},${tb}`;
        if (autoToolTip && !Object.hasOwnProperty.call(toolTipData,ttKey)) {
          toolTipData[ttKey] = { label: tb, data: [] };
          autBundleTT[ttKey] = true;
        }
        if (!s[tb]) {
          b.push(tb);
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        s[tb] || (s[tb] = [])
        s[tb].push(link.i);
        if (autBundleTT[ttKey]) {
          toolTipData[ttKey].data?.push(link.l || link.i);
        }
      }
    }
    this.links = links;
    this.nodes = nodes;
    this.nodeLinks = nodeLinks;
  }

  moveNode(id: string, p: Cordinate) {
    if (this.noChange) return;
    this.fit = false;
    const node = this.nodes[id];
    const diff = {
      x: p.x - this.nodes[id].x,
      y: p.y - this.nodes[id].y,
    };
    const list: string[] = node.g && this.dragGroups[node.g] || [id];

    for (let i = 0; i<list.length; ++i) {
      const gid = list[i];
      const node=this.nodes[gid];
      if (gid != id) {
        this.nodes[gid] = { 
          ...node, 
          x: node.x + diff.x,
          y: node.y + diff.y, 
        };
      } else {
        this.nodes[gid] = { ...node, x: p.x, y: p.y };
      }
      const np=this.changes.nodes[gid] = {x:node.x,y:node.y};
      if (!this.drag) {
        this.needsIndexing = true;
        if (!node.h) {
          const box = this.createNodeBox(np, this.r);
          this.buildIndex(box, 'nodes', node);
        }
        this.rebuild = true;
        for (const [key, link] of Object.entries(this.nodeToLinkMap[node.i])) {
          this.indexer.cleaIndex("links", key);
          this.drawLink(link);
          delete this.linkCache[key];
        }
        this.rebuild = false;
      } else {

        this.doDraw = false;
        this.rebuild = true;
        for (const link of Object.values(this.nodeToLinkMap[node.i])) {
          this.drawLink(link);
        }
        this.rebuild = false;
        this.doDraw = true;
      }
    }
    this.needsMinMax = true;
    this.draw();
  }

  moveLink(res: PointLookupResult, o: Cordinate) {
    const list: NodeEl[] = [];

    if (res.type == 'link') {
      list.push(this.nodes[res.link!.l.s]);
      list.push(this.nodes[res.link!.l.d]);
    } else {
      list.push(this.nodes[res.bundle!.s]);
      list.push(this.nodes[res.bundle!.d]);
    }
    for (let id = 0; id < list.length; ++id) {
      const { x, y, i } = list[id];
      const p: Cordinate = {
        x: x + o.x,
        y: y + o.y,
      }
      this.moveNode(i, p);
    }
  }

  moveCanvas(p: Cordinate) {
    if (this.noChange) return;
    const { x, y } = p;
    this.transform.x = x
    this.transform.y = y;
    this.fit = false;
    this.draw();
  }

  translateCanvasYX(p: Cordinate, t = this.transform) {
    const mod = 1 / t.k;
    const px = p.x - t.x;
    const py = p.y - t.y;
    const x = px * mod
    const y = py * mod;
    return { x, y } as Cordinate
  }
}
